import { server } from '../core/server/axios';
import { BACKEND_URL } from '@/core/config';

/**
 * Менеджер встреч — модуль «Meeting».
 *
 * Все даты, которые отдаёт сервер, — UTC (с суффиксом Z). В браузере их разбирает dayjs
 * в локальный пояс пользователя, а на сервер время уходит обратно через toISOString().
 * Перечисления передаются числами.
 */

export enum MeetingStatus {
    Scheduled = 0,
    Deal = 1,
    Refused = 2,
    Rescheduled = 3,
    NoShow = 4,
}

export enum MeetingClientType {
    Individual = 0,
    LegalEntity = 1,
}

export interface MeetingClientModel {
    id: string;
    fullName: string;
    phone: string;
    iin?: string | null;
    clientType: MeetingClientType;
    position?: string | null;
    contactPersonName?: string | null;
}

export interface MeetingProductModel {
    id: string;
    name: string;
    description: string;
}

export interface MeetingProductRequest {
    name: string;
    description?: string;
}

export interface MeetingProductSummary {
    id: string;
    name: string;
}

export interface MeetingModel {
    id: string;
    client: MeetingClientModel;
    place: string;
    scheduledAt: string;
    purpose: string;
    products: MeetingProductSummary[];
    assignedTo: string;
    assignedToName: string;
    assignedBy: string;
    assignedByName: string;
    status: MeetingStatus;
    resultComment?: string | null;
    resultAt?: string | null;
    createdAt: string;
}

export interface MeetingSaveRequest {
    /** Существующий клиент; вместо него можно передать поля нового клиента */
    clientId?: string;
    newClientFullName?: string;
    newClientPhone?: string;
    newClientIin?: string;
    newClientType?: MeetingClientType;
    newClientPosition?: string;
    newClientContactPersonName?: string;
    place: string;
    /** ISO-время в UTC, например dayjs().toISOString() */
    scheduledAt: string;
    purpose: string;
    assignedTo: string;
    /** При изменении встречи undefined оставляет продукты как есть */
    productIds?: string[];
}

export interface MeetingStatusRequest {
    status: MeetingStatus;
    comment?: string;
}

export interface MeetingListParams {
    scope?: 'mine' | 'all';
    status?: MeetingStatus;
    from?: string;
    to?: string;
    employeeLogin?: string;
    page?: number;
    pageSize?: number;
}

export interface MeetingPage<T> {
    page: number;
    pageSize: number;
    total: number;
    items: T[];
}

export interface MeetingStatusCount {
    status: MeetingStatus;
    count: number;
}

export interface MeetingTrendPoint {
    /** Календарный день по Алматы, yyyy-MM-dd */
    date: string;
    scheduled: number;
    deal: number;
    refused: number;
    rescheduled: number;
    noShow: number;
}

export interface MeetingEmployeeStat {
    login: string;
    fullName: string;
    total: number;
    deal: number;
    refused: number;
    rescheduled: number;
    noShow: number;
    conversionRate: number;
}

export interface MeetingAnalyticsSummary {
    total: number;
    scheduled: number;
    deal: number;
    refused: number;
    rescheduled: number;
    noShow: number;
    conversionRate: number;
}

export interface MeetingAnalyticsModel {
    from: string;
    to: string;
    summary: MeetingAnalyticsSummary;
    statusBreakdown: MeetingStatusCount[];
    trend: MeetingTrendPoint[];
    /** Только в аналитике по всем сотрудникам */
    byEmployee?: MeetingEmployeeStat[] | null;
}

export class MeetingController {
    async create(request: MeetingSaveRequest): Promise<MeetingModel> {
        return server.post('/Meeting', request, { baseURL: BACKEND_URL });
    }

    async get(id: string): Promise<MeetingModel> {
        return server.get(`/Meeting/${id}`, { baseURL: BACKEND_URL });
    }

    async update(id: string, request: MeetingSaveRequest): Promise<MeetingModel> {
        return server.put(`/Meeting/${id}`, request, { baseURL: BACKEND_URL });
    }

    async updateStatus(id: string, request: MeetingStatusRequest): Promise<MeetingModel> {
        return server.patch(`/Meeting/${id}/status`, request, { baseURL: BACKEND_URL });
    }

    async remove(id: string): Promise<void> {
        return server.delete(`/Meeting/${id}`, { baseURL: BACKEND_URL });
    }

    async list(params: MeetingListParams): Promise<MeetingPage<MeetingModel>> {
        return server.get('/Meeting/list', {
            baseURL: BACKEND_URL,
            params: {
                scope: params.scope ?? 'mine',
                status: params.status ?? undefined,
                from: params.from ?? undefined,
                to: params.to ?? undefined,
                employeeLogin: params.employeeLogin ?? undefined,
                page: params.page ?? 1,
                pageSize: params.pageSize ?? 10,
            },
        });
    }

    /** from/to — ISO-время (UTC) границ периода */
    async calendar(from: string, to: string, login?: string): Promise<MeetingModel[]> {
        return server.get('/Meeting/calendar', {
            baseURL: BACKEND_URL,
            params: { from, to, login: login ?? undefined },
        });
    }

    /** from/to — календарные даты yyyy-MM-dd (по Алматы), обе включительно */
    async getMyAnalytics(from: string, to: string): Promise<MeetingAnalyticsModel> {
        return server.get('/Meeting/analytics/my', { baseURL: BACKEND_URL, params: { from, to } });
    }

    async getAllAnalytics(from: string, to: string, employeeLogin?: string): Promise<MeetingAnalyticsModel> {
        return server.get('/Meeting/analytics/all', {
            baseURL: BACKEND_URL,
            params: { from, to, employeeLogin: employeeLogin ?? undefined },
        });
    }

    async searchClients(q: string, page = 1, pageSize = 10): Promise<MeetingPage<MeetingClientModel>> {
        return server.get('/Meeting/clients/search', { baseURL: BACKEND_URL, params: { q, page, pageSize } });
    }

    async listProducts(): Promise<MeetingProductModel[]> {
        return server.get('/Meeting/products', { baseURL: BACKEND_URL });
    }

    async createProduct(request: MeetingProductRequest): Promise<MeetingProductModel> {
        return server.post('/Meeting/products', request, { baseURL: BACKEND_URL });
    }

    async updateProduct(id: string, request: MeetingProductRequest): Promise<MeetingProductModel> {
        return server.put(`/Meeting/products/${id}`, request, { baseURL: BACKEND_URL });
    }

    async deleteProduct(id: string): Promise<void> {
        return server.delete(`/Meeting/products/${id}`, { baseURL: BACKEND_URL });
    }
}
